/**
 * ContractProcessingStatus - Indicador visual de progresso para processamento assíncrono
 * Exibe stepper horizontal com 3 etapas: Enfileirado → Processando → Concluído
 * Inclui detecção de timeout (2min) com botão de reenvio manual.
 *
 * RELEASE: v1.1.0 - 2026-02-16
 * v1.1.0 - Adicionado timeout detection + botão Tentar Novamente
 * v1.0.0 - Stepper visual básico
 */

import React, { useMemo } from 'react';
import { CheckCircle2, Loader2, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// =============================================================================
// TIPOS
// =============================================================================

/** Tipo da ação em processamento */
type ProcessingAction = 'pdf' | 'signature';

/** Status de processamento suportados */
type ProcessingStatus =
  | 'queued_pdf'
  | 'processing_pdf'
  | 'queued_signature'
  | 'processing_signature';

interface ContractProcessingStatusProps {
  /** Status atual do contrato */
  status: ProcessingStatus;
  /** Tipo da ação (derivado automaticamente do status) */
  action?: ProcessingAction;
  /** Timestamp de quando foi enfileirado (ISO string) */
  queuedAt?: string | null;
  /** Callback para re-enfileirar a ação manualmente */
  onRetry?: () => void;
  /** Indica se o retry está em andamento */
  isRetrying?: boolean;
}

// =============================================================================
// CONFIGURAÇÕES DOS PASSOS
// =============================================================================

/** Definição de cada passo do stepper */
interface StepConfig {
  label: string;
  description: string;
}

/** Passos por tipo de ação */
const STEPS: Record<ProcessingAction, StepConfig[]> = {
  pdf: [
    { label: 'Enfileirado', description: 'Aguardando processamento' },
    { label: 'Gerando PDF', description: 'Convertendo documento' },
    { label: 'Concluído', description: 'PDF disponível' },
  ],
  signature: [
    { label: 'Enfileirado', description: 'Aguardando envio' },
    { label: 'Enviando', description: 'Comunicando com provider' },
    { label: 'Concluído', description: 'Enviado para assinatura' },
  ],
};

/** Mapeia status → índice do passo ativo (0-based) */
const STATUS_TO_STEP: Record<ProcessingStatus, number> = {
  queued_pdf: 0,
  processing_pdf: 1,
  queued_signature: 0,
  processing_signature: 1,
};

/** Mapeia status → tipo de ação */
const STATUS_TO_ACTION: Record<ProcessingStatus, ProcessingAction> = {
  queued_pdf: 'pdf',
  processing_pdf: 'pdf',
  queued_signature: 'signature',
  processing_signature: 'signature',
};

/** Tempo limite em milissegundos antes de mostrar aviso de timeout (2 minutos) */
const TIMEOUT_THRESHOLD_MS = 2 * 60 * 1000;

// =============================================================================
// COMPONENTE
// =============================================================================

/**
 * Indicador visual de progresso com stepper de 3 etapas.
 * Mostra ícone animado no passo ativo e check verde nos concluídos.
 * Detecta timeout de 2 minutos e oferece botão de reenvio manual.
 */
export function ContractProcessingStatus({
  status,
  queuedAt,
  onRetry,
  isRetrying,
}: ContractProcessingStatusProps) {
  const action = STATUS_TO_ACTION[status];
  const activeStep = STATUS_TO_STEP[status];
  const steps = STEPS[action];

  /** Verifica se ultrapassou o tempo limite de processamento */
  const isTimedOut = useMemo(() => {
    if (!queuedAt) return false;
    const elapsed = Date.now() - new Date(queuedAt).getTime();
    return elapsed > TIMEOUT_THRESHOLD_MS;
  }, [queuedAt]);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      {/* Stepper horizontal */}
      <div className="flex items-center justify-between gap-2">
        {steps.map((step, index) => {
          /** Passo já concluído */
          const isCompleted = index < activeStep;
          /** Passo ativo no momento */
          const isActive = index === activeStep;
          /** Passo futuro */
          const isFuture = index > activeStep;

          return (
            <React.Fragment key={step.label}>
              {/* Ícone + Label do passo */}
              <div className="flex flex-col items-center gap-1.5 min-w-0">
                <div
                  className={cn(
                    'flex items-center justify-center h-8 w-8 rounded-full border-2 transition-all',
                    isCompleted && 'border-green-500 bg-green-500/10',
                    isActive && !isTimedOut && 'border-primary bg-primary/10 animate-pulse',
                    isActive && isTimedOut && 'border-amber-500 bg-amber-500/10',
                    isFuture && 'border-muted bg-muted/50'
                  )}
                >
                  {isCompleted && (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                  {isActive && !isTimedOut && (
                    <Loader2 className="h-4 w-4 text-primary animate-spin" />
                  )}
                  {isActive && isTimedOut && (
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                  )}
                  {isFuture && (
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <span
                  className={cn(
                    'text-xs font-medium text-center leading-tight',
                    isCompleted && 'text-green-600 dark:text-green-400',
                    isActive && !isTimedOut && 'text-primary',
                    isActive && isTimedOut && 'text-amber-600 dark:text-amber-400',
                    isFuture && 'text-muted-foreground'
                  )}
                >
                  {step.label}
                </span>
              </div>

              {/* Linha conectora entre passos */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 rounded-full mt-[-1.25rem]',
                    index < activeStep ? 'bg-green-500' : 'bg-muted'
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Texto informativo ou aviso de timeout */}
      {isTimedOut ? (
        <div className="space-y-2">
          <p className="text-xs text-amber-600 dark:text-amber-400 text-center font-medium">
            O processamento está demorando mais que o esperado
          </p>
          {onRetry && (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={onRetry}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Tentar Novamente
            </Button>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center">
          Geralmente leva menos de 1 minuto
        </p>
      )}
    </div>
  );
}
